import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, Check,
  Briefcase, GraduationCap, Zap, TrendingUp,
  BookOpen, Award, Clock,
} from 'lucide-react';
import { toast } from 'sonner';
import { useHeroImage } from '../hooks/usePageHero';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ─── Design tokens ─────────────────────────────────────────────────────────── */
const SERIF   = "'Newsreader', Georgia, serif";
const SANS    = "'Public Sans', system-ui, sans-serif";
const NAVY    = '#16304a';
const TEAL    = '#008C86';
const GOLD    = '#c79a3a';
const PAPER   = '#f7f3ea';
const PAPER2  = '#fbf9f3';
const BONE    = '#f0e9dc';
const BORDER  = '#e2d8c4';
const BORDER2 = '#ece5d6';
const MUTED   = '#6c6258';
const FAINT   = '#7d9bb0';
const WHITE   = '#ffffff';

/* ─── Static data ───────────────────────────────────────────────────────────── */
const TRUST_STATS = [
  { num: '45K+',    label: 'Subscribers nationwide' },
  { num: 'Tuesday', label: 'Every week, on time' },
  { num: '7',       label: 'Opportunity categories' },
  { num: 'Free',    label: 'Always free to subscribe' },
];

const RECEIVES = [
  { Icon: Briefcase,     title: 'Hot Jobs',                   desc: 'In-demand positions across sectors — curated, not scraped.' },
  { Icon: GraduationCap, title: 'Internship Opportunities',   desc: 'Student and early-career openings at top organizations.' },
  { Icon: Zap,           title: 'Gig Work',                   desc: 'Contract, freelance, and project-based opportunities now open.' },
  { Icon: TrendingUp,    title: 'Employer Hiring Moves',      desc: 'Who\'s hiring, expanding, and opening new talent pipelines.' },
  { Icon: ArrowRight,    title: 'Career Pathway Updates',     desc: 'Programs, tracks, and pathways that accelerate your move.' },
  { Icon: BookOpen,      title: 'Training & Certifications',  desc: 'New credentials and upskilling leads worth your time.' },
  { Icon: Clock,         title: 'Opportunity Deadlines',      desc: 'Application windows, grant deadlines, and time-sensitive alerts.' },
];

const BUILT_FOR = [
  { label: 'Job Seekers',              desc: 'Active and passive candidates ready for their next move.' },
  { label: 'Students & Interns',       desc: 'Early-career talent building their first real experience.' },
  { label: 'Early-Career Talent',      desc: 'New graduates stepping into opportunity with intention.' },
  { label: 'Career Changers',          desc: 'Professionals pivoting with purpose and a clear direction.' },
  { label: 'Opportunity Trackers',     desc: 'Strategic professionals following where real movement is happening.' },
];

const TESTIMONIALS = [
  { quote: '"Opportunity Unlocked lands every Tuesday and I actually use it. Real jobs, real leads, no noise."',                                              attr: 'Senior Operations Manager',    loc: 'Washington, D.C.' },
  { quote: '"Found my current role through an alert in this newsletter. The career pathway section is the part I share with everyone on my team."',         attr: 'Program Manager',              loc: 'Chicago, IL' },
  { quote: '"The internship section alone makes it worth subscribing. I forward it to my students every single Tuesday without fail."',                      attr: 'Career Services Director',     loc: 'Atlanta, GA' },
];

/* ─── Scoped responsive CSS (ou-* namespace) ─────────────────────────────────── */
const STYLES = `
  @media (max-width: 960px) {
    .ou-hero-grid     { grid-template-columns: 1fr !important; }
    .ou-receives-grid { grid-template-columns: repeat(2,1fr) !important; }
    .ou-built-grid    { grid-template-columns: repeat(2,1fr) !important; }
    .ou-choice-grid   { grid-template-columns: 1fr !important; }
    .ou-bottom-grid   { grid-template-columns: 1fr !important; }
    .ou-testi-grid    { grid-template-columns: 1fr !important; }
    .ou-trust-grid    { grid-template-columns: repeat(2,1fr) !important; }
    .ou-h1            { font-size: 58px !important; letter-spacing: -2.5px !important; }
  }
  @media (max-width: 600px) {
    .ou-receives-grid { grid-template-columns: 1fr !important; }
    .ou-built-grid    { grid-template-columns: 1fr !important; }
    .ou-trust-grid    { grid-template-columns: 1fr !important; }
    .ou-h1            { font-size: 42px !important; letter-spacing: -1.5px !important; }
  }
`;

/* ─── Page component ─────────────────────────────────────────────────────────── */
export default function NewsletterPage() {
  const heroImg = useHeroImage('newsletter');
  const [email, setEmail]           = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone]             = useState(false);
  const [consent, setConsent]       = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email.trim() || !consent) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/newsletter/subscribe`, { email: email.trim(), source_page: 'newsletter', consent: true });
      setDone(true);
      setEmail('');
      toast.success("You're in. Opportunity Unlocked arrives every Tuesday.");
    } catch (err) {
      const detail = err?.response?.data?.detail || '';
      if (detail.toLowerCase().includes('already')) {
        setDone(true);
        toast.info("You're already subscribed.");
      } else {
        toast.error('Could not subscribe. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ background: PAPER, fontFamily: SANS, color: NAVY, lineHeight: 1.5, WebkitFontSmoothing: 'antialiased' }}
      data-testid="newsletter-page">
      <style>{STYLES}</style>

      {/* ══════════════════════════════════════════════════════════════════════
          HERO
      ══════════════════════════════════════════════════════════════════════ */}
      <section style={{
        background: heroImg ? `url(${heroImg}) center/cover no-repeat` : PAPER,
        borderBottom: `1px solid ${BORDER}`,
      }}>
        <div className="container-custom" style={{ paddingTop: 60, paddingBottom: 60 }}>
          <div
            style={{ display: 'grid', gridTemplateColumns: '1fr 360px', gap: 48, alignItems: 'center' }}
            className="ou-hero-grid"
          >
            {/* Text column */}
            <div>
              {/* Eyebrow */}
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: TEAL, display: 'inline-block', boxShadow: `0 0 0 3px rgba(0,140,134,.22)` }}/>
                <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '1.8px', textTransform: 'uppercase', color: TEAL, fontFamily: SANS }}
                  data-testid="newsletter-eyebrow">
                  OPPORTUNITY POST PRESENTS
                </span>
              </div>

              {/* H1 */}
              <h1
                style={{ fontFamily: SERIF, fontWeight: 500, fontSize: 76, lineHeight: 0.92, color: NAVY, margin: '0 0 20px', letterSpacing: '-3px' }}
                className="ou-h1"
                data-testid="newsletter-heading"
              >
                Opportunity<br />Unlocked
              </h1>

              {/* Subhead */}
              <p style={{ fontSize: 18, color: NAVY, lineHeight: 1.55, maxWidth: 500, margin: '0 0 10px', fontFamily: SANS, fontWeight: 500 }}>
                Weekly jobs, internships, gig work, and career opportunities — unlocked every Tuesday.
              </p>

              {/* Supporting line */}
              <p style={{ fontSize: 14.5, color: MUTED, lineHeight: 1.65, maxWidth: 500, margin: '0 0 12px', fontFamily: SANS }}>
                The openings, employer moves, programs, and next-step opportunities worth paying attention to.
              </p>

              {/* Brand line */}
              <p style={{ fontFamily: SERIF, fontStyle: 'italic', fontSize: 16.5, color: GOLD, margin: '0 0 28px' }}>
                Where merit is recognized. Fairness is expected. Opportunity finds its audience.
              </p>

              {/* CTAs */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 18 }}>
                <button
                  onClick={() => document.getElementById('ou-subscribe')?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: TEAL, color: WHITE, border: 'none', borderRadius: 7, padding: '13px 24px', fontWeight: 700, fontSize: 14, cursor: 'pointer', fontFamily: SANS }}
                  data-testid="newsletter-subscribe-cta"
                >
                  Unlock Opportunities <ArrowRight size={14}/>
                </button>
                <Link
                  to="/career-hub"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'transparent', color: NAVY, border: `1.5px solid ${BORDER}`, borderRadius: 7, padding: '12px 20px', fontWeight: 600, fontSize: 13.5, textDecoration: 'none', fontFamily: SANS }}
                  data-testid="newsletter-career-hub-cta"
                >
                  Browse Career Hub
                </Link>
              </div>

              {/* Trust line */}
              <p style={{ fontSize: 12.5, color: MUTED, margin: 0, fontFamily: SANS }}>
                Join 45,000+ subscribers tracking real opportunity every week.
              </p>
            </div>

            {/* Edition preview card */}
            <div style={{
              background: NAVY, borderRadius: 16,
              overflow: 'hidden', boxShadow: '0 24px 64px rgba(22,48,74,.2)',
            }}
              data-testid="newsletter-edition-card"
            >
              {/* Card header */}
              <div style={{ padding: '16px 22px', borderBottom: '1px solid rgba(255,255,255,.08)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase', color: 'rgba(255,255,255,.45)', fontFamily: SANS }}>Weekly Edition</span>
                <span style={{ fontSize: 10.5, color: 'rgba(255,255,255,.35)', fontFamily: SANS }}>Every Tuesday</span>
              </div>
              {/* Card title */}
              <div style={{ padding: '18px 22px 20px' }}>
                <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '1.4px', textTransform: 'uppercase', color: TEAL, fontFamily: SANS, marginBottom: 6 }}>
                  Opportunity Post
                </div>
                <div style={{ fontFamily: SERIF, fontWeight: 500, fontSize: 26, color: WHITE, lineHeight: 1.1, marginBottom: 4 }}>
                  Opportunity Unlocked
                </div>
                <div style={{ fontSize: 11.5, color: 'rgba(255,255,255,.38)', fontFamily: SANS }}>
                  Jobs · Internships · Gig Work · Career Moves
                </div>
              </div>
              {/* Teal accent band */}
              <div style={{ background: `linear-gradient(135deg, ${TEAL} 0%, #005e5a 100%)`, height: 64, position: 'relative', overflow: 'hidden', margin: '0 16px', borderRadius: 8 }}>
                <div style={{ position: 'absolute', top: -14, right: -14, width: 72, height: 72, borderRadius: '50%', background: 'rgba(255,255,255,.08)' }}/>
                {[0,1,2,3,4,5,6].map(i => (
                  <div key={i} style={{ position: 'absolute', bottom: 8, left: 14 + i*16, width: 4, height: [12,20,16,26,18,14,22][i], background: 'rgba(255,255,255,.3)', borderRadius: 2 }}/>
                ))}
              </div>
              {/* What's inside preview */}
              <div style={{ padding: '18px 22px 20px' }}>
                <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.3px', color: GOLD, marginBottom: 10, fontFamily: SANS }}>
                  This week's opportunities:
                </div>
                {['Hot jobs across all sectors', 'Internship and gig openings', 'Employer hiring moves to know', 'Deadlines and program alerts'].map((item, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 7 }}>
                    <div style={{ width: 5, height: 5, borderRadius: '50%', background: TEAL, flexShrink: 0 }}/>
                    <span style={{ fontSize: 12.5, color: 'rgba(255,255,255,.72)', fontFamily: SANS }}>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          TRUST BAR
      ══════════════════════════════════════════════════════════════════════ */}
      <div style={{ background: WHITE, borderBottom: `1px solid ${BORDER}` }} data-testid="newsletter-trust-bar">
        <div className="container-custom">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', textAlign: 'center' }} className="ou-trust-grid">
            {TRUST_STATS.map(({ num, label }, i) => (
              <div key={num} style={{ padding: '20px 16px', borderRight: i < TRUST_STATS.length - 1 ? `1px solid ${BORDER}` : 'none' }}>
                <div style={{ fontFamily: SANS, fontWeight: 700, fontSize: 28, color: NAVY, lineHeight: 1, marginBottom: 4 }}>{num}</div>
                <div style={{ fontSize: 12, color: MUTED, fontFamily: SANS }}>{label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="container-custom" style={{ paddingTop: 52, paddingBottom: 60 }}>

        {/* ══════════════════════════════════════════════════════════════════
            WHAT YOU RECEIVE
        ══════════════════════════════════════════════════════════════════ */}
        <section style={{ marginBottom: 44 }} data-testid="newsletter-receives">
          <div style={{ marginBottom: 28 }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '1.6px', textTransform: 'uppercase', color: TEAL, marginBottom: 8, fontFamily: SANS }}>
              WHAT'S IN EVERY EDITION
            </div>
            <h2 style={{ fontWeight: 700, fontSize: 24, color: NAVY, margin: 0, lineHeight: 1.2, letterSpacing: '-0.02em' }}>
              Seven categories. One focused briefing.
            </h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 16 }} className="ou-receives-grid">
            {RECEIVES.map(({ Icon, title, desc }, i) => (
              <div
                key={title}
                style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 12, padding: '20px 20px 18px', display: 'flex', gap: 16, alignItems: 'flex-start' }}
                data-testid={`newsletter-receive-${i}`}
              >
                <div style={{ width: 40, height: 40, borderRadius: 10, background: BONE, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, border: `1px solid ${BORDER2}` }}>
                  <Icon size={17} style={{ color: TEAL }}/>
                </div>
                <div>
                  <div style={{ fontSize: 13.5, fontWeight: 700, color: NAVY, marginBottom: 5, fontFamily: SANS }}>{title}</div>
                  <p style={{ fontSize: 12.5, color: MUTED, lineHeight: 1.55, margin: 0, fontFamily: SANS }}>{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ══════════════════════════════════════════════════════════════════
            BUILT FOR
        ══════════════════════════════════════════════════════════════════ */}
        <section style={{ marginBottom: 44 }} data-testid="newsletter-built-for">
          <div style={{ marginBottom: 22 }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '1.6px', textTransform: 'uppercase', color: TEAL, marginBottom: 8, fontFamily: SANS }}>
              BUILT FOR
            </div>
            <h2 style={{ fontWeight: 700, fontSize: 22, color: NAVY, margin: 0, letterSpacing: '-0.018em' }}>
              If you're in motion, this is for you.
            </h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 12 }} className="ou-built-grid">
            {BUILT_FOR.map(({ label, desc }) => (
              <div
                key={label}
                style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 12, padding: '18px 16px 16px' }}
                data-testid={`newsletter-built-${label.replace(/\s+/g,'-').toLowerCase()}`}
              >
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: TEAL, marginBottom: 10 }}/>
                <div style={{ fontSize: 13.5, fontWeight: 700, color: NAVY, marginBottom: 5, fontFamily: SANS, lineHeight: 1.2 }}>{label}</div>
                <p style={{ fontSize: 12, color: MUTED, lineHeight: 1.55, margin: 0, fontFamily: SANS }}>{desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ══════════════════════════════════════════════════════════════════
            NEWSLETTER CHOICE
        ══════════════════════════════════════════════════════════════════ */}
        <section style={{ marginBottom: 44 }} data-testid="newsletter-choice">
          <div style={{ marginBottom: 22, display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '1.6px', textTransform: 'uppercase', color: GOLD, marginBottom: 8, fontFamily: SANS }}>YOUR BRIEFING. YOUR CHOICE.</div>
              <h2 style={{ fontWeight: 700, fontSize: 22, color: NAVY, margin: 0, letterSpacing: '-0.018em' }}>One subscription. Two ways to stay ahead.</h2>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 14 }} className="ou-choice-grid">

            {/* Opportunity Unlocked card */}
            <div style={{ background: WHITE, border: `2px solid ${TEAL}`, borderRadius: 14, padding: '28px 28px 24px', position: 'relative' }}
              data-testid="newsletter-choice-unlocked">
              <div style={{ position: 'absolute', top: 16, right: 18, background: TEAL, color: WHITE, borderRadius: 20, padding: '3px 10px', fontSize: 10, fontWeight: 700, letterSpacing: '0.8px', textTransform: 'uppercase', fontFamily: SANS }}>
                This page
              </div>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '1.4px', textTransform: 'uppercase', color: TEAL, marginBottom: 8, fontFamily: SANS }}>JOBS & OPPORTUNITIES</div>
              <h3 style={{ fontWeight: 800, fontSize: 22, color: NAVY, margin: '0 0 8px', lineHeight: 1.1, letterSpacing: '-0.025em' }}>Opportunity Unlocked</h3>
              <div style={{ fontSize: 12, fontWeight: 600, color: FAINT, fontFamily: SANS, marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.6px' }}>Every Tuesday</div>
              <p style={{ fontSize: 14, color: MUTED, lineHeight: 1.6, margin: '0 0 20px', fontFamily: SANS }}>
                Weekly jobs, internships, gig work, and career opportunities — the openings worth paying attention to.
              </p>
              <button
                onClick={() => document.getElementById('ou-subscribe')?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: TEAL, color: WHITE, border: 'none', borderRadius: 6, padding: '10px 18px', fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: SANS }}
                data-testid="newsletter-choice-unlocked-cta"
              >
                Subscribe Free <ArrowRight size={12}/>
              </button>
            </div>

            {/* The Week Ahead card */}
            <div style={{ background: PAPER2, border: `1px solid ${BORDER}`, borderRadius: 14, padding: '28px 28px 24px' }}
              data-testid="newsletter-choice-twa">
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '1.4px', textTransform: 'uppercase', color: FAINT, marginBottom: 8, fontFamily: SANS }}>INTELLIGENCE BRIEFING</div>
              <h3 style={{ fontWeight: 800, fontSize: 22, color: NAVY, margin: '0 0 8px', lineHeight: 1.1, letterSpacing: '-0.025em' }}>The Week Ahead</h3>
              <div style={{ fontSize: 12, fontWeight: 600, color: FAINT, fontFamily: SANS, marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.6px' }}>Every Friday</div>
              <p style={{ fontSize: 14, color: MUTED, lineHeight: 1.6, margin: '0 0 20px', fontFamily: SANS }}>
                Executive-level macro, policy, and opportunity intelligence — enter every weekend with full situational awareness.
              </p>
              <Link
                to="/the-week-ahead"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: NAVY, color: WHITE, borderRadius: 6, padding: '10px 18px', fontWeight: 700, fontSize: 13, textDecoration: 'none', fontFamily: SANS }}
                data-testid="newsletter-choice-twa-cta"
              >
                Explore The Week Ahead <ArrowRight size={12}/>
              </Link>
            </div>
          </div>

          {/* Get Both CTA */}
          <div style={{ background: BONE, border: `1px solid ${BORDER}`, borderRadius: 10, padding: '18px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}
            data-testid="newsletter-get-both">
            <div>
              <span style={{ fontSize: 13.5, fontWeight: 700, color: NAVY, fontFamily: SANS }}>Get both briefings free.</span>
              <span style={{ fontSize: 13.5, color: MUTED, fontFamily: SANS, marginLeft: 8 }}>
                Subscribe to Opportunity Unlocked now, then explore The Week Ahead at your own pace.
              </span>
            </div>
            <button
              onClick={() => document.getElementById('ou-subscribe')?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: GOLD, color: WHITE, border: 'none', borderRadius: 6, padding: '10px 18px', fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: SANS, whiteSpace: 'nowrap', flexShrink: 0 }}
              data-testid="newsletter-get-both-cta"
            >
              Get Both Free <ArrowRight size={12}/>
            </button>
          </div>
        </section>

        {/* ══════════════════════════════════════════════════════════════════
            SUBSCRIBE SECTION
        ══════════════════════════════════════════════════════════════════ */}
        <section
          style={{ background: NAVY, borderRadius: 16, padding: '40px', marginBottom: 44, position: 'relative', overflow: 'hidden' }}
          id="ou-subscribe"
          data-testid="newsletter-subscribe-section"
        >
          <div style={{ position: 'absolute', top: -24, right: -24, width: 130, height: 130, borderRadius: '50%', background: 'rgba(0,140,134,.14)' }} aria-hidden="true"/>
          <div style={{ position: 'absolute', bottom: -32, left: -32, width: 110, height: 110, borderRadius: '50%', background: 'rgba(199,154,58,.1)' }} aria-hidden="true"/>
          <div style={{ position: 'relative', zIndex: 1, maxWidth: 620, margin: '0 auto', textAlign: 'center' }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '1.6px', textTransform: 'uppercase', color: TEAL, marginBottom: 12, fontFamily: SANS }}>
              OPPORTUNITY UNLOCKED
            </div>
            <h2 style={{ fontWeight: 800, fontSize: 28, color: WHITE, margin: '0 0 10px', lineHeight: 1.15, letterSpacing: '-0.03em' }}>
              Real opportunities. Every Tuesday.
            </h2>
            <p style={{ fontSize: 14.5, color: 'rgba(255,255,255,.6)', lineHeight: 1.65, margin: '0 0 24px', fontFamily: SANS }}>
              Jobs, internships, gig work, employer moves, deadlines, and career pathway updates — one email per week.
            </p>

            <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 24px', display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '8px 20px' }}>
              {['Free to subscribe', 'Delivered every Tuesday', 'No spam', 'Unsubscribe anytime'].map(item => (
                <li key={item} style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, color: 'rgba(255,255,255,.72)', fontFamily: SANS }}>
                  <div style={{ width: 16, height: 16, borderRadius: '50%', background: TEAL, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Check size={9} color={WHITE}/>
                  </div>
                  {item}
                </li>
              ))}
            </ul>

            {done ? (
              <div style={{ background: 'rgba(0,140,134,.22)', border: `1.5px solid rgba(0,140,134,.5)`, borderRadius: 8, padding: '14px 22px', display: 'inline-flex', alignItems: 'center', gap: 10 }}
                data-testid="newsletter-subscribe-success">
                <Check size={16} color={TEAL}/>
                <span style={{ fontSize: 14, color: 'rgba(255,255,255,.88)', fontWeight: 600, fontFamily: SANS }}>
                  You're in. Opportunity Unlocked arrives every Tuesday.
                </span>
              </div>
            ) : (
              <form
                onSubmit={handleSubscribe}
                style={{ maxWidth: 480, margin: '0 auto 10px' }}
                data-testid="newsletter-subscribe-form"
              >
                <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
                  <input
                    type="email"
                    required
                    placeholder="Enter your email address"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    style={{ flex: 1, border: '1.5px solid rgba(255,255,255,.15)', borderRadius: 7, padding: '13px 16px', fontSize: 14, background: 'rgba(255,255,255,.08)', color: WHITE, fontFamily: SANS, outline: 'none' }}
                    data-testid="newsletter-email-input"
                  />
                  <button
                    type="submit"
                    disabled={submitting || !consent}
                    style={{ background: TEAL, color: WHITE, border: 'none', borderRadius: 7, padding: '13px 22px', fontWeight: 700, fontSize: 14, cursor: (submitting || !consent) ? 'not-allowed' : 'pointer', fontFamily: SANS, whiteSpace: 'nowrap', opacity: (submitting || !consent) ? 0.55 : 1, transition: 'opacity .2s' }}
                    data-testid="newsletter-subscribe-submit"
                  >
                    {submitting ? 'Joining…' : 'Unlock Opportunities'}
                  </button>
                </div>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 9, cursor: 'pointer', textAlign: 'left' }}>
                  <input
                    type="checkbox"
                    checked={consent}
                    onChange={e => setConsent(e.target.checked)}
                    data-testid="newsletter-consent-checkbox"
                    style={{ marginTop: 3, flexShrink: 0, accentColor: TEAL, width: 14, height: 14 }}
                  />
                  <span style={{ fontSize: 12, color: 'rgba(255,255,255,.58)', fontFamily: SANS, lineHeight: 1.6 }}>
                    Yes, send me Opportunity Unlocked — career opportunities, jobs, and internships every Tuesday. I can unsubscribe at any time.
                  </span>
                </label>
              </form>
            )}
          </div>
        </section>

        {/* ══════════════════════════════════════════════════════════════════
            CAREER HUB BRIDGE
        ══════════════════════════════════════════════════════════════════ */}
        <section style={{ marginBottom: 44 }} data-testid="newsletter-career-hub-bridge">
          <div style={{
            background: `linear-gradient(135deg, ${TEAL} 0%, #005e5a 100%)`,
            borderRadius: 14, padding: '36px 40px',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 24, flexWrap: 'wrap',
          }}>
            <div>
              <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase', color: 'rgba(255,255,255,.55)', marginBottom: 8, fontFamily: SANS }}>
                OPPORTUNITY POST
              </div>
              <h2 style={{ fontWeight: 800, fontSize: 24, color: WHITE, margin: '0 0 8px', lineHeight: 1.15, letterSpacing: '-0.025em' }}>
                Looking for opportunities right now?
              </h2>
              <p style={{ fontSize: 14, color: 'rgba(255,255,255,.72)', lineHeight: 1.6, margin: 0, fontFamily: SANS, maxWidth: 480 }}>
                Don't wait for Tuesday. Browse active jobs, internships, and gig work on the Career Hub — updated continuously.
              </p>
            </div>
            <Link
              to="/career-hub"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: WHITE, color: NAVY, borderRadius: 7, padding: '13px 24px', fontWeight: 700, fontSize: 14, textDecoration: 'none', fontFamily: SANS, whiteSpace: 'nowrap', flexShrink: 0 }}
              data-testid="newsletter-ch-bridge-btn"
            >
              Browse Career Hub <ArrowRight size={14}/>
            </Link>
          </div>
        </section>

        {/* ══════════════════════════════════════════════════════════════════
            TESTIMONIALS
        ══════════════════════════════════════════════════════════════════ */}
        <section style={{ marginBottom: 44 }} data-testid="newsletter-testimonials">
          <h2 style={{ fontWeight: 700, fontSize: 22, color: NAVY, margin: '0 0 20px', textAlign: 'center', letterSpacing: '-0.018em' }}>
            What readers are saying
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 16 }} className="ou-testi-grid">
            {TESTIMONIALS.map(({ quote, attr, loc }, i) => (
              <div key={i} style={{ background: NAVY, borderRadius: 12, padding: '26px 24px', position: 'relative', overflow: 'hidden' }}>
                <div style={{ position: 'absolute', top: -6, left: 16, fontFamily: SERIF, fontSize: 88, color: 'rgba(255,255,255,.05)', lineHeight: 1, userSelect: 'none' }} aria-hidden="true">"</div>
                <p style={{ fontFamily: SERIF, fontStyle: 'italic', fontSize: 15.5, color: 'rgba(255,255,255,.88)', lineHeight: 1.65, margin: '0 0 18px', position: 'relative', zIndex: 1 }}>{quote}</p>
                <div>
                  <div style={{ fontSize: 12.5, fontWeight: 700, color: WHITE, fontFamily: SANS }}>{attr}</div>
                  <div style={{ fontSize: 11.5, color: 'rgba(255,255,255,.38)', fontFamily: SANS, marginTop: 2 }}>{loc}</div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ══════════════════════════════════════════════════════════════════
            BOTTOM: ADVERTISE + WEEK AHEAD BRIDGE
        ══════════════════════════════════════════════════════════════════ */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }} className="ou-bottom-grid">

          {/* Advertise card */}
          <div style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 14, padding: '32px', overflow: 'hidden', position: 'relative' }}
            data-testid="newsletter-advertise">
            <div style={{ position: 'absolute', top: -28, right: -28, width: 130, height: 130, borderRadius: '50%', background: `radial-gradient(circle, ${GOLD}22 0%, transparent 70%)` }} aria-hidden="true"/>
            <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase', color: GOLD, marginBottom: 10, fontFamily: SANS }}>For Advertisers & Partners</div>
            <h2 style={{ fontWeight: 800, fontSize: 22, color: NAVY, margin: '0 0 12px', lineHeight: 1.15, letterSpacing: '-0.025em' }}>Reach a high-intent audience.</h2>
            <p style={{ fontSize: 14, color: MUTED, lineHeight: 1.65, margin: '0 0 22px', fontFamily: SANS }}>
              Position your brand alongside trusted opportunity intelligence reaching 45,000+ job seekers, career changers, and early-career professionals nationwide.
            </p>
            <Link
              to="/contact"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: NAVY, color: WHITE, borderRadius: 6, padding: '11px 18px', fontWeight: 700, fontSize: 13, textDecoration: 'none', fontFamily: SANS }}
              data-testid="newsletter-media-kit-btn"
            >
              Request Media Kit <ArrowRight size={12}/>
            </Link>
          </div>

          {/* The Week Ahead bridge card */}
          <div style={{ background: PAPER2, border: `1px solid ${BORDER}`, borderRadius: 14, padding: '32px' }}
            data-testid="newsletter-twa-bridge">
            <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase', color: FAINT, marginBottom: 10, fontFamily: SANS }}>Also from Opportunity Post</div>
            <h2 style={{ fontWeight: 800, fontSize: 22, color: NAVY, margin: '0 0 12px', lineHeight: 1.15, letterSpacing: '-0.025em' }}>The Week Ahead</h2>
            <p style={{ fontSize: 14, color: MUTED, lineHeight: 1.65, margin: '0 0 22px', fontFamily: SANS }}>
              Already getting Opportunity Unlocked? Upgrade your Friday with The Week Ahead — the executive briefing for macro, policy, and opportunity intelligence.
            </p>
            <Link
              to="/the-week-ahead"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: NAVY, color: WHITE, borderRadius: 6, padding: '11px 18px', fontWeight: 700, fontSize: 13, textDecoration: 'none', fontFamily: SANS }}
              data-testid="newsletter-twa-bridge-btn"
            >
              Explore The Week Ahead <ArrowRight size={12}/>
            </Link>
          </div>
        </div>

      </div>
    </div>
  );
}
